import React from "react";
import { useDroppable } from "@dnd-kit/core";
import {
  GridCell,
  PlacedShowWrapper,
  RemoveButton,
  StateButton,
} from "./ScheduleBoard.styles";
import ShowBox from "./ShowBox";
import { PlacedItem, ShowState, placedItemTitle } from "./types";

interface DroppableCellProps {
  cellId: string;
  monthStart: boolean;
  occupant?: PlacedItem;
  occupantState?: ShowState;
  occupantStateKey?: string;
  occupantCount?: number;
  occupantNextAirDate?: string;
  whiteBlocks?: boolean;
  activeOriginCellId: string | null;
  onRemove: (cellId: string) => void;
  onOpenStateMenu: (
    key: string,
    current: ShowState,
    x: number,
    y: number
  ) => void;
}

const DroppableCell = ({
  cellId,
  monthStart,
  occupant,
  occupantState,
  occupantStateKey,
  occupantCount,
  occupantNextAirDate,
  whiteBlocks,
  activeOriginCellId,
  onRemove,
  onOpenStateMenu,
}: DroppableCellProps): React.JSX.Element => {
  const { setNodeRef, isOver } = useDroppable({
    id: `cell:${cellId}`,
    data: { type: "cell", cellId },
  });

  const isOccupied = Boolean(occupant);
  // Hovering an occupied cell is a rejected drop, unless it is the cell the
  // dragged item already lives in (dropping back onto itself is a no-op).
  const invalidOver = isOver && isOccupied && activeOriginCellId !== cellId;
  const validOver = isOver && !invalidOver;

  return (
    <GridCell
      ref={setNodeRef}
      $isOver={validOver}
      $invalidOver={invalidOver}
      $occupied={isOccupied}
      $monthStart={monthStart}
    >
      {occupant && (
        <PlacedShowWrapper>
          {occupantStateKey && (
            <StateButton
              type="button"
              aria-label={`Change state of ${placedItemTitle(occupant)}`}
              onPointerDown={(e): void => e.stopPropagation()}
              onClick={(e): void => {
                e.stopPropagation();
                onOpenStateMenu(
                  occupantStateKey,
                  occupantState ?? "regular",
                  e.clientX,
                  e.clientY
                );
              }}
            >
              ✳
            </StateButton>
          )}
          <ShowBox
            id={`placed:${cellId}`}
            title={placedItemTitle(occupant)}
            subtitle={
              occupant.kind === "show"
                ? occupant.show.curators?.[0]?.name
                : undefined
            }
            state={occupantState}
            count={occupant.kind === "show" ? occupantCount : undefined}
            nextAirDate={occupantNextAirDate}
            whiteBlocks={whiteBlocks}
            data={{ type: "placed", item: occupant, fromCellId: cellId }}
          />
          <RemoveButton
            type="button"
            aria-label={`Remove ${placedItemTitle(occupant)}`}
            onPointerDown={(e): void => e.stopPropagation()}
            onClick={(e): void => {
              e.stopPropagation();
              onRemove(cellId);
            }}
          >
            ×
          </RemoveButton>
        </PlacedShowWrapper>
      )}
    </GridCell>
  );
};

export default DroppableCell;
